import React, { useCallback, useMemo, useState } from "react";
import MdaeNumericBox from "../numeric-box";
import MdaePopover from "../popover";
import MdaeList from "../list";
import { IMdaeCurrencyBoxProps } from "./index";
import "./index.scss";

// Native DevExtreme currency mask: shows up to 2 decimals and trims trailing zeros.
const CURRENCY_FORMAT = "$ #,##0.##";

// Coerce a possibly-string value into a number (or null) for the NumberBox.
const toNumber = (v: any) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

// Format a dropdown option label as currency (only used for the small suggestion list).
const formatAmountLabel = (v: any) => {
  const n = Number(v || 0);
  return (
    "$ " +
    n.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  );
};

// A currency field backed by a DevExtreme NumberBox (native formatting) plus a click-to-open
// dropdown of suggested values. The NumberBox's default valueChangeEvent is "change", so
// typing never fires onValueChanged — the value is committed (via onValueCommitted) only on
// blur or when a dropdown value is picked. This keeps fast, native input handling without a
// per-keystroke round-trip to the consuming store. See Bug 140628.
const MdaeCurrencyBox: React.FC<IMdaeCurrencyBoxProps> = React.memo((props) => {
  const {
    id,
    title,
    value: amount,
    dataSourceValues,
    onValueCommitted,
    readOnly,
    required,
    titleComponent,
    onKeyDown,
    children,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerId = useMemo(() => `${id}CurrencyBox`, [id]);

  const value = useMemo(() => toNumber(amount), [amount]);
  const options = useMemo(() => dataSourceValues || [], [dataSourceValues]);
  const hasOptions = options.length > 0;

  const listDataSource = useMemo(
    () => options.map((v) => ({ value: v, label: formatAmountLabel(v) })),
    [options]
  );
  const listHeight = useMemo(
    () => Math.min((listDataSource.length || 1) * 40, 200),
    [listDataSource]
  );

  // Open the suggestion dropdown when the user clicks the field (matches openOnFieldClick).
  const handleFieldClick = useCallback(() => {
    if (hasOptions && !readOnly) setDropdownOpen(true);
  }, [hasOptions, readOnly]);

  const handleValueChanged = useCallback(
    (e) => {
      // e.event is present only for user-driven commits (blur/enter), not programmatic ones.
      if (e.event) onValueCommitted?.(e.value);
    },
    [onValueCommitted]
  );

  const handleItemClick = useCallback(
    (e) => {
      onValueCommitted?.(e.itemData.value);
      setDropdownOpen(false);
    },
    [onValueCommitted]
  );

  const handlePopoverHidden = useCallback(() => setDropdownOpen(false), []);

  const popoverPosition = useMemo(
    () => ({ my: "top" as const, at: "bottom" as const, of: `#${containerId}` }),
    [containerId]
  );
  const popoverWidth = useCallback(
    () => document.getElementById(containerId)?.offsetWidth || 250,
    [containerId]
  );

  return (
    <div
      id={containerId}
      className={"mdae-currency-box"}
      style={{ position: "relative" }}
      onClick={handleFieldClick}
    >
      <MdaeNumericBox
        id={id}
        mdaeNumberBoxTitle={title}
        MdaeNumberBoxTitleComponent={titleComponent || undefined}
        value={value}
        format={CURRENCY_FORMAT}
        showSpinButtons={false}
        readOnly={readOnly}
        required={required}
        onKeyDown={onKeyDown}
        onValueChanged={handleValueChanged}
      >
        {children}
      </MdaeNumericBox>
      {hasOptions && (
        <MdaePopover
          wrapperAttr={{ class: "mdae-currency-box-dropdown" }}
          target={`#${containerId}`}
          visible={dropdownOpen}
          onHidden={handlePopoverHidden}
          position={popoverPosition}
          width={popoverWidth}
          hideOnOutsideClick={true}
          shading={false}
        >
          <MdaeList
            id={`${id}DropdownList`}
            dataSource={listDataSource}
            displayExpr="label"
            selectionMode="single"
            focusStateEnabled={false}
            height={listHeight}
            onItemClick={handleItemClick}
          />
        </MdaePopover>
      )}
    </div>
  );
});

export default MdaeCurrencyBox;
